import ROLE_HOME from './roleHome';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { BranchProvider } from './contexts/BranchContext';
import { Toaster } from 'react-hot-toast';

// Страницы
import Login from './pages/Login';
import PlatformPage from './pages/Platform';
import AiSettingsPage from './pages/AiSettings';
import ManagerPage from './pages/Manager';
import TechnologPage from './pages/Technolog';
import QualityPage from './pages/Quality';
import ProductionRequestsPage from './pages/ProductionRequests';
import OperatorTerminal from './pages/OperatorTerminal';
import WarehousePage from './pages/Warehouse';
import TrackingPage from './pages/Tracking';
import TabloPage from './pages/Tablo';
import AiApp from './pages/AiApp';
import SheetQR from './pages/SheetQR';
import ProfilePage from './pages/Profile';
import AdminPage from './pages/Admin';
import ProductionBoard from './pages/ProductionBoard';
import IntegrationPage from './pages/Integration';
import DirectorHub from './pages/DirectorHub';
import StockMovementPage from './pages/StockMovement';
import SheetTerminal from './pages/SheetTerminal';
import SessionHistoryPage from './pages/SessionHistory';
import AuditLogPage from './pages/AuditLog';
import BuhgalterPage from './pages/Buhgalter';
import RegistryPage from './pages/Registry';
import ReportsPage from './pages/Reports';
import OrdersByDayPage from './pages/OrdersByDay';
import AiBackBar from './ai/AiBackBar';

// Роли и их маршруты по умолчанию
// Раздел поставщика программы. Не по роли: «админ» есть и у клиента, а это
// другой уровень, а не более старший. Нет права — уводим как с любого
// несуществующего адреса: отказ по правам сам по себе сообщение о том, что
// раздел есть.
const PlatformRoute = ({ children }) => {
  const token = sessionStorage.getItem('access_token');
  if (!token) return <Navigate to="/login" replace />;
  if (sessionStorage.getItem('is_platform') !== '1') {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Настройки ИИ — только владелец платформы (is_superuser); остальных уводим как
// с несуществующего адреса.
const SuperuserRoute = ({ children }) => {
  const token = sessionStorage.getItem('access_token');
  if (!token) return <Navigate to="/login" replace />;
  if (sessionStorage.getItem('is_superuser') !== '1') {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Роли с рабочим местом. Инвестор в ROLE_HOME есть (его дом — /ai), но общие
// экраны «для всех» (щит, профиль) ему не открываем.
const WORK_ROLES = Object.keys(ROLE_HOME).filter((r) => r !== 'investor');

const ProtectedRoute = ({ children, allowedRoles, allowPlan }) => {
  const token = sessionStorage.getItem('access_token');
  const role = sessionStorage.getItem('role');
  // Планировать цех — право личное, а не должность: галочку «Планировщик»
  // ставят человеку, кем бы он ни числился. Сервер это знал давно и по этому
  // же праву пускал согласовывать заказы и выписывать заявку. А дверь на
  // экран открывалась по роли — и плановика с ролью менеджера разворачивало
  // на его собственный список заказов. Права у него были, войти он не мог.
  const canPlan = sessionStorage.getItem('can_plan') === '1';

  if (!token) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(role) && !(allowPlan && canPlan)) {
    return <Navigate to={ROLE_HOME[role] || '/login'} replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
    <BranchProvider>
    <Router>
      <Toaster position="top-right" />
      <AiBackBar />
      <Routes>
        {/* Публичные маршруты */}
        <Route path="/login" element={<Login />} />
        {/* Клиент без ссылки набирает короткий код руками */}
        <Route path="/track" element={<TrackingPage />} />
        <Route path="/track/:trackingCode" element={<TrackingPage />} />
        {/* Телевизор в офисе. Ключ в адресе: /tablo?k=… — логина у экрана нет */}
        {/* PWA руководства «LamTech AI»: свой вход, свой manifest */}
        <Route path="/ai" element={<AiApp />} />
        <Route path="/tablo" element={<TabloPage />} />
        <Route path="/sheet" element={<SheetQR />} />
        <Route path="/sheet/:qrCode" element={<SheetQR />} />
        {/* Короткий путь для QR на бирке: каждый лишний символ в ссылке —
            это лишние модули в коде, а бирка всего 23 мм.
            Совпадение без учёта регистра, поэтому «/S/KAKVAT7» тоже сюда. */}
        <Route path="/s/:qrCode" element={<SheetQR />} />

        {/* Менеджер по продажам */}
        <Route path="/manager" element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <ManagerPage />
          </ProtectedRoute>
        } />

        {/* Технолог — управление очередью */}
        <Route path="/technolog" element={
          <ProtectedRoute allowedRoles={['technolog', 'admin']} allowPlan>
            <TechnologPage />
          </ProtectedRoute>
        } />

        {/* Технолог по качеству — разбор брака по каждому листу.
            Он работает удалённо и в цех не заходит: справочники, эталонные
            режимы и заявки ему не открыты вовсе, у него один экран. Технолог
            цеха и директор смотрят тот же разбор — им он тоже нужен, просто
            не единственный. */}
        <Route path="/quality" element={
          <ProtectedRoute allowedRoles={['quality', 'technolog', 'director', 'admin', 'investor']}>
            <QualityPage />
          </ProtectedRoute>
        } />

        {/* Заявка на производство — печать плана смены */}
        <Route path="/production-requests" element={
          <ProtectedRoute allowedRoles={['technolog', 'admin']} allowPlan>
            <ProductionRequestsPage />
          </ProtectedRoute>
        } />

        {/* Оператор пресса — терминал */}
        <Route path="/terminal" element={
          <ProtectedRoute allowedRoles={['operator', 'admin']}>
            <OperatorTerminal />
          </ProtectedRoute>
        } />

        {/* Кладовщик СГП */}
        <Route path="/warehouse" element={
          <ProtectedRoute allowedRoles={['sklad_gp', 'admin']}>
            <WarehousePage />
          </ProtectedRoute>
        } />

        {/* Терминал пересортицы — отдельный полноэкранный режим для планшета:
            меню и шапка склада тут только мешают, экран держат одной рукой */}
        <Route path="/sheet-terminal" element={
          <ProtectedRoute allowedRoles={['sklad_gp', 'controller', 'admin']}>
            <SheetTerminal />
          </ProtectedRoute>
        } />
        {/* Дашборд директора: сводка, разбор и прессы — разделами внутри */}
        <Route path="/director" element={
          <ProtectedRoute allowedRoles={['director', 'admin']}>
            <DirectorHub />
          </ProtectedRoute>
        } />

        

        

        {/* Бухгалтер: обмен с 1С, рецепты, остатки под сверку */}
        <Route path="/buhgalter" element={
          <ProtectedRoute allowedRoles={['buhgalter', 'admin']}>
            <BuhgalterPage />
          </ProtectedRoute>
        } />

        {/* Профиль */}
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={WORK_ROLES}>
            <ProfilePage />
          </ProtectedRoute>
        } />

        {/* Администратор */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPage />
          </ProtectedRoute>
        } />

        {/* Производственный щит — все роли.
            Менеджера тут не было: считалось, что продажам прессы не нужны.
            Но планирование лежит на менеджере с правом «планировщик», а
            планируют, глядя на прессы. Пункт меню без маршрута уходил бы на
            вход — поэтому право и маршрут правятся вместе. */}
        <Route path="/board" element={
          <ProtectedRoute allowedRoles={WORK_ROLES}>
            <ProductionBoard />
          </ProtectedRoute>
        } />

        {/* 1С Интеграция — только admin */}
        <Route path="/integration" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <IntegrationPage />
          </ProtectedRoute>
        } />

        {/* Движение ТМЗ (ТЗ §4 - сверка с 1С) */}
        <Route path="/stock-movement" element={
          <ProtectedRoute allowedRoles={['admin', 'director', 'sklad_gp', 'buhgalter', 'investor']}>
            <StockMovementPage />
          </ProtectedRoute>
        } />

        {/* История смен: цепочка «пресс → склад» */}
        <Route path="/sessions" element={
          <ProtectedRoute allowedRoles={['admin', 'director', 'sklad_gp', 'technolog', 'buhgalter', 'investor']}>
            <SessionHistoryPage />
          </ProtectedRoute>
        } />

        {/* Отчёты одной дверью: их смотрят, когда нужно свериться, а не
            когда работают — в меню им место не рядом с работой */}
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={['admin', 'director', 'technolog', 'buhgalter', 'sklad_gp', 'investor']}>
            <ReportsPage />
          </ProtectedRoute>
        } />

        {/* Заказы по дням: что приняли за период, с группировкой по выбору */}
        <Route path="/orders-by-day" element={
          <ProtectedRoute allowedRoles={['admin', 'director', 'technolog', 'buhgalter', 'sklad_gp', 'investor']}>
            <OrdersByDayPage />
          </ProtectedRoute>
        } />

        {/* Реестр выпуска: от декора к смене, от смены к режиму и листам.
            Смотрят все, кто отвечает за качество и остаток; меняет — никто. */}
        <Route path="/registry" element={
          <ProtectedRoute allowedRoles={['admin', 'director', 'technolog', 'buhgalter', 'sklad_gp', 'investor']}>
            <RegistryPage />
          </ProtectedRoute>
        } />

        {/* Журнал действий — администратор и директор */}
        <Route path="/audit" element={
          <ProtectedRoute allowedRoles={['admin', 'director']}>
            <AuditLogPage />
          </ProtectedRoute>
        } />

        {/* Редирект с корня */}
        <Route path="/platform" element={
          <PlatformRoute>
            <PlatformPage />
          </PlatformRoute>
        } />

        <Route path="/platform/ai" element={
          <SuperuserRoute>
            <AiSettingsPage />
          </SuperuserRoute>
        } />

        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
    </BranchProvider>
    </ThemeProvider>
  );
}

export default App;
